const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'libs/common_web/src/actions/metakatActions.ts';
import Imm from 'immutable';
import { KISAPI as API } from '../middleware/kisapi';
import {
    apiDeleteMetaKatRecord,
    apiLoad1MetaKatRecord,
    apiLoadMetaKatBlock,
    apiLoadMetaKatByLookup,
    apiLoadMetaKatChildren,
    apiLoadMetaKatRecordByKatFkey,
    apiMultiLoadMetaKatRecords,
    apiUpdateMetaKatRecord,
    MetaKatFields,
} from './kisapi/metaKat';
import { IKISAPIMeta, KisApiAction, kisapiActionTypes } from '../defs/actionTypes';

/**
 * Action types
 */
export const actionTypes = {
    loadMetaKatBlock: kisapiActionTypes('LOADING_METAKAT_BLOCK'),
    loadMetaKatChildren: kisapiActionTypes('LOAD_METAKAT_CHILDREN'),
    loadMetaKatRecord: kisapiActionTypes('LOADING_METAKAT_RECORD'),
    updateMetaKatRecord: kisapiActionTypes('COMMON_WEB_UPDATE_METAKAT_RECORD'),
    loadMetaKatLookup: kisapiActionTypes('LOAD_METAKAT_LOOKUP'),
    deleteMetaKatRecord: kisapiActionTypes('DELETE_METAKAT_RECORD'),
    forgetMetaKatRecord: 'FORGET_METAKAT_BLOCK',
    loadMetaKatRecordByKatFkey: kisapiActionTypes('LOAD_METAKAT_BY_KAT_FKEY'),
    multiloadMetaKatRecords: kisapiActionTypes('LOAD_MULTI_METAKAT_RECORDS'),
    loadReaIpsConfig: kisapiActionTypes('LOAD_REAIPSCONFIG'),
    loadStatusTagConfig: kisapiActionTypes('LOAD_STATUSTAGCONFIG'),
    loadMainTopicConfig: kisapiActionTypes('LOAD_MAINTOPICCONFIG'),
};

export interface ILoadMetaKatChildrenMeta extends IKISAPIMeta {
    pcode: string;
    lookup: boolean;
    rootRecordExpectedInPayload: boolean;
}

export interface ILoadMetaKatLookupMeta extends IKISAPIMeta {
    icode: string;
}

/**
 * Action to load a metakat block via icode.
 */
export function loadMetaKatBlock(
    icode: string,
    lookup: boolean = false,
    additionalFields: string[] = [],
    suppressErrorMsg = false,
    fields: string[] = ['ICODE', 'PCODE', 'KAT', 'FKEY', 'TYP', 'NAME', 'POS', 'STATUS', 'PARAMICODE', 'LEVEL']
) {
    const fieldsToLoad = fields.concat(additionalFields);
    return {
        type: actionTypes.loadMetaKatBlock.combined,
        [API]: apiLoadMetaKatBlock(icode, fieldsToLoad, lookup),
        meta: {
            icode,
            lookup,
            rootRecordExpectedInPayload: true,
            showDlgWhenResultHasMoreRows: true,
            suppressErrorMsg,
            fieldsToLoad,
        },
    };
}

export type ILoadMetaKatBlockAction = KisApiAction<typeof loadMetaKatBlock>;

/**
 * Action to load the children of a given parent MetaKat entry.
 */
export function loadMetaKatChildren(pcode: string, fields: string[] | MetaKatFields, lookup: boolean = false) {
    const meta: ILoadMetaKatChildrenMeta = {
        pcode,
        lookup,
        rootRecordExpectedInPayload: false,
        showDlgWhenResultHasMoreRows: true,
    };
    return {
        type: actionTypes.loadMetaKatChildren.combined,
        [API]: apiLoadMetaKatChildren(pcode, fields, lookup),
        meta,
    };
}

export type ILoadMetaKatChildrenAction = KisApiAction<typeof loadMetaKatChildren>;

/**
 * Generic action to load a single record from MetaKat.
 * @param icode (in): The ICODE of the record to be loaded.
 * @param fields (in): Array of fields you're interested in.
 * @param lookup (in): set to true if a lookup should be performed
 * array is passed in, the action will load all the fields including CONTENT.
 */
export function loadMetaKatRecord(icode: string, fields: string[] | MetaKatFields, lookup?: boolean) {
    return {
        type: actionTypes.loadMetaKatRecord.combined,
        [API]: apiLoad1MetaKatRecord(icode, fields, lookup),
        meta: { icode },
    };
}

export type ILoadMetaKatRecordAction = KisApiAction<typeof loadMetaKatRecord>;

/**
 * Action to look up the actual ICODE from a virtual ICODE
 */
export function loadMetaKatLookup(icode: string) {
    const meta: ILoadMetaKatLookupMeta = {
        icode,
    };
    return {
        type: actionTypes.loadMetaKatLookup.combined,
        [API]: apiLoadMetaKatByLookup(icode),
        meta,
    };
}

export type ILoadMetaKatLookupAction = KisApiAction<typeof loadMetaKatLookup>;

/**
 * Action to delete a metakat record
 * @param icode ICODE of the block to delete
 * @param etag ETAG of the block to delete
 * @return An action
 */
export function deleteMetaKatRecord(icode: string, etag: string) {
    return {
        type: actionTypes.deleteMetaKatRecord.combined,
        [API]: apiDeleteMetaKatRecord(icode, etag),
        meta: {
            icode,
            etag,
        },
    };
}

export function updateMetaKatRecord(icode: string, etag: string, rec: Imm.Map<string, any>) {
    return {
        type: actionTypes.updateMetaKatRecord.combined,
        [API]: apiUpdateMetaKatRecord(rec.set('ICODE', icode).set('ETAG', etag)),
        meta: { icode, etag, rec },
    };
}

export function forgetMetaKatRecord(icode: string) {
    return {
        type: actionTypes.forgetMetaKatRecord,
        meta: { icode },
    };
}

export type IForgetMetaKatRecordAction = KisApiAction<typeof forgetMetaKatRecord>;

export function actionLoadMetaKatRecordByKatFkey(
    pcode: string,
    kat: string,
    fkey: string,
    checkDate: boolean = false,
    lookup: boolean = false,
    disallowArrays: boolean = true,
    fields: string[] | MetaKatFields = MetaKatFields.ALL_EXCLUDING_CONTENT,
    status?: string
) {
    return {
        type: actionTypes.loadMetaKatRecordByKatFkey.combined,
        [API]: apiLoadMetaKatRecordByKatFkey(pcode, kat, fkey, checkDate, lookup, disallowArrays, fields, status),
        meta: { pcode, kat, fkey, checkDate, fields, lookup, disallowArrays, status, suppressErrorMsg: false },
    };
}

export type LoadMetaKatRecordByKatFkeyAction = KisApiAction<typeof actionLoadMetaKatRecordByKatFkey>;

/**
 * Action to load ReaIps configuration.
 * @param callerId optional identifier that may be used to identify the action via meta from sagas, etc.
 */
export function actionLoadReaIpsConfig(callerId?: string) {
    const icode = 'b58d9bf9-e204-475f-9332-6c626babaea7';
    return {
        type: actionTypes.loadReaIpsConfig.combined,
        [API]: apiLoad1MetaKatRecord(icode, ['ICODE', 'CONTENT'], true),
        meta: { icode, callerId },
    };
}

export type LoadReaIpsConfigAction = KisApiAction<typeof actionLoadReaIpsConfig>;

/**
 * Action to load Statustag configuration.
 * @param callerId optional identifier that may be used to identify the action via meta from sagas, etc.
 */
export function actionLoadStatusTagConfig(callerId?: string) {
    const icode = '7652f14d-e731-4b9d-a3da-bb37a6986d21';
    return {
        type: actionTypes.loadStatusTagConfig.combined,
        [API]: apiLoad1MetaKatRecord(icode, ['ICODE', 'CONTENT'], true),
        meta: { icode, callerId },
    };
}

export type LoadStatusTagConfigAction = KisApiAction<typeof actionLoadStatusTagConfig>;

/**
 * Action to load MainTopic configurations
 */
export function loadMainTopicConfig() {
    const pcode = '073b6458-8476-4d4b-adf5-836fe6fd90bb';
    return {
        type: actionTypes.loadMainTopicConfig.combined,
        [API]: apiLoadMetaKatChildren(pcode, ['ICODE', 'KAT', 'FKEY', 'STATUS', 'CONTENT'], true),
        meta: { pcode },
    };
}

export type ILoadMainTopicConfigAction = KisApiAction<typeof loadMainTopicConfig>;

export function loadMultiMetakatRecords(list: string[]) {
    return {
        type: actionTypes.multiloadMetaKatRecords.combined,
        [API]: apiMultiLoadMetaKatRecords(list, ['ICODE', 'PCODE', 'FKEY', 'NAME', 'POS', 'CONTENT']),
        meta: { list },
    };
}

export type ILoadMultiMetakatRecordsAction = KisApiAction<typeof loadMultiMetakatRecords>;
